class ProductRecommendations extends HTMLElement {
  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    this.#loadRecommendationsWhenVisible();
    this.#mutationObserver.observe(this, { attributes: true });
  }

  /**
   * Defers the initial recommendations fetch/render until this element is
   * near the viewport, to avoid rendering an offscreen product grid on page load.
   */
  #loadRecommendationsWhenVisible() {
    const loader = window.GYDeferredLoader;
    if (!loader) {
      this.#loadRecommendations();
      return;
    }

    let hasLoaded = false;
    const loadOnce = () => {
      if (hasLoaded) return;
      hasLoaded = true;
      this.#loadRecommendations();
    };

    loader.onVisible(this, loadOnce, '400px');
    loader.onFirstInteraction(loadOnce, 6000);
    loader.runOnIdle(loadOnce, 7000);
  }

  /**
   * Check if current user is a B2B dealer
   * @returns {boolean}
   */
  #isB2BCustomer() {
    const customerId = this.dataset.customerId;
    const customerTags = this.dataset.customerTags;
    const sparklayerAuth = this.dataset.sparklayerAuth;
    
    if (customerId) {
      if (sparklayerAuth === 'true') return true;
      
      if (customerTags) {
        const tags = customerTags.toLowerCase().split(',').map(t => t.trim());
        const b2bTags = ['b2b', 'wholesale', 'master_dealer', 'distributor'];
        return tags.some(tag => b2bTags.includes(tag));
      }
    }
    
    const recentlyViewedSection = document.querySelector('recently-viewed-products');
    if (recentlyViewedSection) {
      const rvCustomerId = recentlyViewedSection.dataset.customerId;
      const rvCustomerTags = recentlyViewedSection.dataset.customerTags;
      const rvSparklayerAuth = recentlyViewedSection.dataset.sparklayerAuth;
      
      if (!rvCustomerId) return false;
      
      if (rvSparklayerAuth === 'true') return true;
      
      if (rvCustomerTags) {
        const tags = rvCustomerTags.toLowerCase().split(',').map(t => t.trim());
        const b2bTags = ['b2b', 'wholesale', 'master_dealer', 'distributor'];
        return tags.some(tag => b2bTags.includes(tag));
      }
    }
    
    if (window.customer && window.customer.tags) {
      const b2bTags = ['b2b', 'wholesale', 'master_dealer', 'distributor'];
      return window.customer.tags.some(tag => b2bTags.includes(tag.toLowerCase()));
    }
    
    return false;
  }

  /**
   * Filter out restricted products from recommendations
   */
  #filterRestrictedProducts() {
    this.querySelectorAll('.resource-list__item[data-custitem-restricted="true"]').forEach((item) => {
      const parentSlide = item.closest('slideshow-slide');
      if (parentSlide) {
        parentSlide.remove();
      } else {
        item.remove();
      }
    });

    const isDealer = this.#isB2BCustomer();

    if (!isDealer) {
      const productCards = this.querySelectorAll('.resource-list__item');

      productCards.forEach((item) => {
        const productCard = item.querySelector('product-card');
        if (!productCard) return;

        const isRestricted = productCard.dataset.b2bRestricted === 'true';

        if (isRestricted) {
          const parentSlide = item.closest('slideshow-slide');
          if (parentSlide) {
            parentSlide.remove();
          } else {
            item.remove();
          }
        }
      });
    }

    const remainingProducts = this.querySelectorAll('.resource-list__item');
    const remainingCount = remainingProducts.length;

    if (remainingCount === 0) {
      this.style.display = 'none';
    }
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;
          
          setTimeout(() => this.#filterRestrictedProducts(), 0);
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
